// A self-contained RIFF/WAVE decoder for the legacy codecs browsers refuse.
//
// `decodeAudioData` only handles plain PCM (and IEEE float) inside a WAV
// container. Jail-call recorders, DVRs, and dictation devices routinely write
// µ-law, A-law, or ADPCM *inside a .wav file* — those all fail in the browser
// even though the file is perfectly valid. This module decodes them directly.
//
// PURITY CONTRACT: this returns the audio exactly as it was recorded — original
// channel count, original sample rate, no downmix, no resample. The 16 kHz mono
// conversion Whisper needs is destructive and lives in decodeAudio.js. Keep it
// that way: a format-converter tool needs the untouched audio.

const FORMAT_PCM = 0x0001;
const FORMAT_MS_ADPCM = 0x0002;
const FORMAT_IEEE_FLOAT = 0x0003;
const FORMAT_ALAW = 0x0006;
const FORMAT_MULAW = 0x0007;
const FORMAT_OKI_ADPCM = 0x0010;
const FORMAT_IMA_ADPCM = 0x0011;
const FORMAT_DIALOGIC_OKI = 0x0017;
const FORMAT_MPEG = 0x0050;
const FORMAT_MP3 = 0x0055;
const FORMAT_IBM_MULAW = 0x0101;
const FORMAT_IBM_ALAW = 0x0102;
const FORMAT_EXTENSIBLE = 0xfffe;

// Named so an unsupported file produces an error the user can act on, rather
// than a generic failure. Anything listed here but not decodable is a known
// gap; anything absent is genuinely unrecognized.
const FORMAT_NAMES = {
  0x0001: 'PCM',
  0x0002: 'Microsoft ADPCM',
  0x0003: 'IEEE float',
  0x0006: 'A-law (G.711)',
  0x0007: 'µ-law (G.711)',
  0x0010: 'OKI ADPCM',
  0x0011: 'IMA/DVI ADPCM',
  0x0015: 'Digistd',
  0x0016: 'Digifix',
  0x0017: 'Dialogic OKI ADPCM',
  0x0020: 'Yamaha ADPCM',
  0x0022: 'DSP Group Truespeech',
  0x0031: 'GSM 6.10',
  0x0032: 'MSN Audio',
  0x0040: 'G.721 ADPCM',
  0x0045: 'G.726 ADPCM',
  0x0050: 'MPEG audio',
  0x0055: 'MP3',
  0x0064: 'G.721 ADPCM (Antex)',
  0x0083: 'G.729',
  0x0101: 'IBM µ-law',
  0x0102: 'IBM A-law',
  0x0103: 'IBM AVC ADPCM',
  0x0111: 'Vivo G.723',
  0x0130: 'Siren',
  0x0160: 'Windows Media Audio 1',
  0x0161: 'Windows Media Audio 2',
  0x0162: 'Windows Media Audio 9 Pro',
  0x0163: 'Windows Media Audio Lossless',
  0x0164: 'Windows Media Audio Speech',
  0x2000: 'Dolby AC-3',
  0x2222: 'G.729 ("ICMV"/GTL)',
  0xfffe: 'Extensible',
};

// Both tags carry the same 8 kbit/s G.729 bitstream: 0x0083 is the format the
// spec registered; 0x2222 is what Global Tel Link's inmate-call recording
// systems write, branded "ICMV". Decoded by g729Decode.js, not here.
export const G729_FORMAT_TAGS = new Set([0x0083, 0x2222]);

// Codecs decoded to PCM right here.
const SUPPORTED = new Set([
  FORMAT_PCM,
  FORMAT_MS_ADPCM,
  FORMAT_IEEE_FLOAT,
  FORMAT_ALAW,
  FORMAT_MULAW,
  FORMAT_OKI_ADPCM,
  FORMAT_IMA_ADPCM,
  FORMAT_DIALOGIC_OKI,
  FORMAT_IBM_MULAW,
  FORMAT_IBM_ALAW,
]);

// Codecs where the WAV is only a wrapper around a stream the browser already
// decodes natively. We hand the payload straight to decodeAudioData instead of
// implementing a decoder — see extractEmbeddedStream().
const EMBEDDED = new Map([
  [FORMAT_MPEG, 'audio/mpeg'],
  [FORMAT_MP3, 'audio/mpeg'],
]);

export function formatName(tag) {
  return FORMAT_NAMES[tag] || 'unknown';
}

/** Thrown when the container parses but we can't decode the codec inside it. */
export class UnsupportedWavFormatError extends Error {
  constructor(tag) {
    const hex = `0x${tag.toString(16).padStart(4, '0')}`;
    super(`This WAV uses format ${hex} (${formatName(tag)}), which this tool can't decode.`);
    this.name = 'UnsupportedWavFormatError';
    this.formatTag = tag;
  }
}

const clamp = (v, lo, hi) => (v < lo ? lo : v > hi ? hi : v);

// ── G.711 lookup tables ──────────────────────────────────────────────────────
// Built once from the Sun reference implementations. A table lookup is both
// faster and less error-prone than recomputing the bit math per sample.

const MULAW_TABLE = (() => {
  const t = new Float32Array(256);
  for (let i = 0; i < 256; i++) {
    const u = ~i & 0xff;
    let value = ((u & 0x0f) << 3) + 0x84;
    value <<= (u & 0x70) >> 4;
    t[i] = ((u & 0x80) ? 0x84 - value : value - 0x84) / 32768;
  }
  return t;
})();

const ALAW_TABLE = (() => {
  const t = new Float32Array(256);
  for (let i = 0; i < 256; i++) {
    const a = i ^ 0x55;
    let value = (a & 0x0f) << 4;
    const seg = (a & 0x70) >> 4;
    if (seg === 0) value += 8;
    else if (seg === 1) value += 0x108;
    else {
      value += 0x108;
      value <<= seg - 1;
    }
    t[i] = ((a & 0x80) ? value : -value) / 32768;
  }
  return t;
})();

// ── ADPCM tables ─────────────────────────────────────────────────────────────

const IMA_STEP_TABLE = [
  7, 8, 9, 10, 11, 12, 13, 14, 16, 17, 19, 21, 23, 25, 28, 31, 34, 37, 41, 45,
  50, 55, 60, 66, 73, 80, 88, 97, 107, 118, 130, 143, 157, 173, 190, 209, 230,
  253, 279, 307, 337, 371, 408, 449, 494, 544, 598, 658, 724, 796, 876, 963,
  1060, 1166, 1282, 1411, 1552, 1707, 1878, 2066, 2272, 2499, 2749, 3024, 3327,
  3660, 4026, 4428, 4871, 5358, 5894, 6484, 7132, 7845, 8630, 9493, 10442,
  11487, 12635, 13899, 15289, 16818, 18500, 20350, 22385, 24623, 27086, 29794,
  32767,
];

const IMA_INDEX_TABLE = [-1, -1, -1, -1, 2, 4, 6, 8, -1, -1, -1, -1, 2, 4, 6, 8];

// Dialogic/OKI ADPCM is the same shape as IMA but 12-bit, with a shorter step
// table and only 8 index deltas. Used by the Dialogic telephony boards that a
// lot of older inmate-call recording systems were built on.
const OKI_STEP_TABLE = [
  16, 17, 19, 21, 23, 25, 28, 31, 34, 37, 41, 45, 50, 55, 60, 66, 73, 80, 88,
  97, 107, 118, 130, 143, 157, 173, 190, 209, 230, 253, 279, 307, 337, 371, 408,
  449, 494, 544, 598, 658, 724, 796, 876, 963, 1060, 1166, 1282, 1411, 1552,
];

const OKI_INDEX_TABLE = [-1, -1, -1, -1, 2, 4, 6, 8];

const MS_ADAPT_TABLE = [
  230, 230, 230, 230, 307, 409, 512, 614, 768, 614, 512, 409, 307, 230, 230, 230,
];

// The seven predictor coefficient pairs every MS ADPCM encoder ships with. A
// file may override these in the `fmt ` chunk's extra bytes, which is rare —
// handled below when present.
const MS_DEFAULT_COEFS = [
  [256, 0], [512, -256], [0, 0], [192, 64], [240, 0], [460, -208], [392, -232],
];

// ── container parsing ────────────────────────────────────────────────────────

const RIFF = 0x52494646; // 'RIFF'
const WAVE = 0x57415645; // 'WAVE'

/** Cheap sniff so callers can route by content rather than trusting a filename. */
export function isRiffWave(arrayBuffer) {
  if (!arrayBuffer || arrayBuffer.byteLength < 12) return false;
  const dv = new DataView(arrayBuffer);
  return dv.getUint32(0, false) === RIFF && dv.getUint32(8, false) === WAVE;
}

function readFourCC(dv, offset) {
  return String.fromCharCode(
    dv.getUint8(offset), dv.getUint8(offset + 1),
    dv.getUint8(offset + 2), dv.getUint8(offset + 3),
  );
}

/**
 * Walk the RIFF chunk list and pull out the header fields plus the data extent.
 * Chunks we don't care about (LIST, fact, cue) are skipped by declared size.
 */
export function parseWavHeader(arrayBuffer) {
  if (!isRiffWave(arrayBuffer)) {
    throw new Error('Not a RIFF/WAVE file.');
  }

  const dv = new DataView(arrayBuffer);
  const end = arrayBuffer.byteLength;

  let fmt = null;
  let dataOffset = -1;
  let dataLength = 0;

  let pos = 12;
  while (pos + 8 <= end) {
    const id = readFourCC(dv, pos);
    const size = dv.getUint32(pos + 4, true);
    const body = pos + 8;

    if (id === 'fmt ' && size >= 16 && body + 16 <= end) {
      let formatTag = dv.getUint16(body, true);
      const numChannels = dv.getUint16(body + 2, true);
      const sampleRate = dv.getUint32(body + 4, true);
      const blockAlign = dv.getUint16(body + 12, true);
      const bitsPerSample = dv.getUint16(body + 14, true);

      let coefs = null;
      if (formatTag === FORMAT_EXTENSIBLE && size >= 40 && body + 26 <= end) {
        // WAVE_FORMAT_EXTENSIBLE hides the real codec in the first two bytes of
        // the SubFormat GUID. Without this, every extensible file looks unknown.
        formatTag = dv.getUint16(body + 24, true);
      } else if (formatTag === FORMAT_MS_ADPCM && size >= 22 && body + 22 <= end) {
        // Optional per-file predictor coefficients override the standard set.
        const numCoefs = dv.getUint16(body + 20, true);
        if (numCoefs > 0 && body + 22 + numCoefs * 4 <= end) {
          coefs = [];
          for (let i = 0; i < numCoefs; i++) {
            coefs.push([
              dv.getInt16(body + 22 + i * 4, true),
              dv.getInt16(body + 24 + i * 4, true),
            ]);
          }
        }
      }

      fmt = { formatTag, numChannels, sampleRate, blockAlign, bitsPerSample, coefs };
    } else if (id === 'data') {
      dataOffset = body;
      // A streamed or truncated file can declare more data than it carries.
      dataLength = Math.min(size, end - body);
    }

    // Chunks are word-aligned; an odd size carries a pad byte.
    pos = body + size + (size % 2);
  }

  if (!fmt) throw new Error('This WAV file has no format header (missing "fmt " chunk).');
  if (dataOffset < 0) throw new Error('This WAV file has no audio data (missing "data" chunk).');
  if (!fmt.numChannels || !fmt.sampleRate) throw new Error('This WAV file has an invalid format header.');

  return { ...fmt, dataOffset, dataLength };
}

/**
 * Some WAV files are only a container around a stream the browser can already
 * decode (MP3 is the common one). Rather than implement those codecs, hand the
 * payload back so the caller can pass it to decodeAudioData.
 *
 * @returns {{ payload: ArrayBuffer, mimeType: string, formatTag: number } | null}
 */
export function extractEmbeddedStream(arrayBuffer) {
  if (!isRiffWave(arrayBuffer)) return null;

  let header;
  try {
    header = parseWavHeader(arrayBuffer);
  } catch {
    return null;
  }

  const mimeType = EMBEDDED.get(header.formatTag);
  if (!mimeType) return null;

  return {
    payload: arrayBuffer.slice(header.dataOffset, header.dataOffset + header.dataLength),
    mimeType,
    formatTag: header.formatTag,
  };
}

// ── PCM / float ──────────────────────────────────────────────────────────────

function decodePcm(dv, header) {
  const { numChannels, bitsPerSample, dataOffset, dataLength, formatTag } = header;
  const bytesPerSample = Math.ceil(bitsPerSample / 8);
  const frameSize = bytesPerSample * numChannels;
  if (!frameSize) throw new Error('This WAV file has an invalid sample size.');

  const frames = Math.floor(dataLength / frameSize);
  const channels = Array.from({ length: numChannels }, () => new Float32Array(frames));

  const isFloat = formatTag === FORMAT_IEEE_FLOAT;

  for (let f = 0; f < frames; f++) {
    const base = dataOffset + f * frameSize;
    for (let c = 0; c < numChannels; c++) {
      const off = base + c * bytesPerSample;
      let v;
      if (isFloat) {
        v = bitsPerSample === 64 ? dv.getFloat64(off, true) : dv.getFloat32(off, true);
      } else if (bitsPerSample === 8) {
        v = (dv.getUint8(off) - 128) / 128;       // 8-bit PCM is unsigned
      } else if (bitsPerSample === 16) {
        v = dv.getInt16(off, true) / 32768;
      } else if (bitsPerSample === 24) {
        const b0 = dv.getUint8(off), b1 = dv.getUint8(off + 1), b2 = dv.getUint8(off + 2);
        let raw = b0 | (b1 << 8) | (b2 << 16);
        if (raw & 0x800000) raw |= ~0xffffff;      // sign-extend to 32 bits
        v = raw / 8388608;
      } else if (bitsPerSample === 32) {
        v = dv.getInt32(off, true) / 2147483648;
      } else {
        throw new Error(`This WAV file uses ${bitsPerSample}-bit samples, which this tool can't decode.`);
      }
      channels[c][f] = v;
    }
  }

  return channels;
}

function decodeG711(dv, header, table) {
  const { numChannels, dataOffset, dataLength } = header;
  const frames = Math.floor(dataLength / numChannels);
  const channels = Array.from({ length: numChannels }, () => new Float32Array(frames));

  for (let f = 0; f < frames; f++) {
    const base = dataOffset + f * numChannels;
    for (let c = 0; c < numChannels; c++) {
      channels[c][f] = table[dv.getUint8(base + c)];
    }
  }

  return channels;
}

// ── IMA/DVI ADPCM ────────────────────────────────────────────────────────────

function decodeImaAdpcm(dv, header) {
  const { numChannels, blockAlign, dataOffset, dataLength } = header;
  if (!blockAlign) throw new Error('This IMA ADPCM file has an invalid block size.');

  const headerBytes = 4 * numChannels;
  if (blockAlign <= headerBytes) throw new Error('This IMA ADPCM file has an invalid block size.');

  // Each block: a 4-byte state header per channel, then 4-byte groups holding
  // 8 nibbles, interleaved one group per channel at a time.
  const samplesPerBlock = 1 + Math.floor(((blockAlign - headerBytes) * 2) / numChannels);
  const blocks = Math.floor(dataLength / blockAlign);
  const total = blocks * samplesPerBlock;

  const channels = Array.from({ length: numChannels }, () => new Float32Array(total));
  const predictors = new Int32Array(numChannels);
  const indices = new Int32Array(numChannels);

  let out = 0;

  for (let b = 0; b < blocks; b++) {
    const blockStart = dataOffset + b * blockAlign;

    for (let c = 0; c < numChannels; c++) {
      const off = blockStart + c * 4;
      predictors[c] = dv.getInt16(off, true);
      indices[c] = clamp(dv.getUint8(off + 2), 0, 88);
      channels[c][out] = predictors[c] / 32768;   // the header sample is real audio
    }

    let written = 1;
    let pos = blockStart + headerBytes;
    const blockEnd = blockStart + blockAlign;

    while (written < samplesPerBlock && pos + 4 * numChannels <= blockEnd) {
      for (let c = 0; c < numChannels; c++) {
        for (let byte = 0; byte < 4; byte++) {
          const packed = dv.getUint8(pos + byte);
          // Low nibble first — the opposite of MS ADPCM.
          for (let k = 0; k < 2; k++) {
            const nibble = k === 0 ? (packed & 0x0f) : ((packed >> 4) & 0x0f);
            const step = IMA_STEP_TABLE[indices[c]];

            let diff = step >> 3;
            if (nibble & 1) diff += step >> 2;
            if (nibble & 2) diff += step >> 1;
            if (nibble & 4) diff += step;
            if (nibble & 8) diff = -diff;

            predictors[c] = clamp(predictors[c] + diff, -32768, 32767);
            indices[c] = clamp(indices[c] + IMA_INDEX_TABLE[nibble], 0, 88);

            const idx = out + written + byte * 2 + k;
            if (idx < total) channels[c][idx] = predictors[c] / 32768;
          }
        }
        pos += 4;
      }
      written += 8;
    }

    out += samplesPerBlock;
  }

  return channels;
}

// ── Dialogic / OKI ADPCM ─────────────────────────────────────────────────────

function decodeOkiAdpcm(dv, header) {
  const { numChannels, dataOffset, dataLength } = header;

  // No per-block state header: it's a continuous nibble stream, high nibble
  // first, 12-bit output. Mono is overwhelmingly the norm for this codec.
  const samplesPerChannel = Math.floor((dataLength * 2) / numChannels);
  const channels = Array.from({ length: numChannels }, () => new Float32Array(samplesPerChannel));

  const predictors = new Int32Array(numChannels);
  const indices = new Int32Array(numChannels);
  const written = new Int32Array(numChannels);

  let nibbleCount = 0;

  for (let i = 0; i < dataLength; i++) {
    const packed = dv.getUint8(dataOffset + i);

    for (let k = 0; k < 2; k++) {
      const nibble = k === 0 ? ((packed >> 4) & 0x0f) : (packed & 0x0f);
      const c = nibbleCount % numChannels;
      nibbleCount++;

      if (written[c] >= samplesPerChannel) continue;

      const step = OKI_STEP_TABLE[indices[c]];
      let diff = step >> 3;
      if (nibble & 1) diff += step >> 2;
      if (nibble & 2) diff += step >> 1;
      if (nibble & 4) diff += step;
      if (nibble & 8) diff = -diff;

      predictors[c] = clamp(predictors[c] + diff, -2048, 2047);   // 12-bit range
      indices[c] = clamp(indices[c] + OKI_INDEX_TABLE[nibble & 0x07], 0, OKI_STEP_TABLE.length - 1);

      channels[c][written[c]] = predictors[c] / 2048;
      written[c]++;
    }
  }

  return channels;
}

// ── Microsoft ADPCM ──────────────────────────────────────────────────────────

function decodeMsAdpcm(dv, header) {
  const { numChannels, blockAlign, dataOffset, dataLength, coefs } = header;
  if (!blockAlign) throw new Error('This MS ADPCM file has an invalid block size.');

  const coefTable = coefs && coefs.length ? coefs : MS_DEFAULT_COEFS;
  const headerBytes = 7 * numChannels;
  if (blockAlign <= headerBytes) throw new Error('This MS ADPCM file has an invalid block size.');

  // Two seed samples come from the block header, then one sample per nibble.
  const samplesPerBlock = 2 + Math.floor(((blockAlign - headerBytes) * 2) / numChannels);
  const blocks = Math.floor(dataLength / blockAlign);
  const total = blocks * samplesPerBlock;

  const channels = Array.from({ length: numChannels }, () => new Float32Array(total));

  let out = 0;

  for (let b = 0; b < blocks; b++) {
    const blockStart = dataOffset + b * blockAlign;
    const state = [];

    // The header is laid out field-by-field across channels, not channel-by-
    // channel: all predictors, then all deltas, then all sample1, then sample2.
    let p = blockStart;
    for (let c = 0; c < numChannels; c++) {
      const idx = clamp(dv.getUint8(p + c), 0, coefTable.length - 1);
      state.push({ coef1: coefTable[idx][0], coef2: coefTable[idx][1], delta: 0, s1: 0, s2: 0 });
    }
    p += numChannels;
    for (let c = 0; c < numChannels; c++) state[c].delta = dv.getInt16(p + c * 2, true);
    p += numChannels * 2;
    for (let c = 0; c < numChannels; c++) state[c].s1 = dv.getInt16(p + c * 2, true);
    p += numChannels * 2;
    for (let c = 0; c < numChannels; c++) state[c].s2 = dv.getInt16(p + c * 2, true);
    p += numChannels * 2;

    // Seed samples are emitted oldest-first.
    const written = new Int32Array(numChannels);
    for (let c = 0; c < numChannels; c++) {
      channels[c][out] = state[c].s2 / 32768;
      if (out + 1 < total) channels[c][out + 1] = state[c].s1 / 32768;
      written[c] = 2;
    }

    const blockEnd = blockStart + blockAlign;
    let nibbleCount = 0;

    while (p < blockEnd) {
      const packed = dv.getUint8(p++);
      // High nibble first — the opposite of IMA.
      for (let k = 0; k < 2; k++) {
        const nibble = k === 0 ? ((packed >> 4) & 0x0f) : (packed & 0x0f);
        // For stereo the nibbles alternate channels, one sample each.
        const c = nibbleCount % numChannels;
        nibbleCount++;

        if (written[c] >= samplesPerBlock) continue;
        const st = state[c];

        const signed = nibble > 7 ? nibble - 16 : nibble;
        let predicted = (st.s1 * st.coef1 + st.s2 * st.coef2) >> 8;
        predicted = clamp(predicted + signed * st.delta, -32768, 32767);

        st.s2 = st.s1;
        st.s1 = predicted;
        st.delta = (MS_ADAPT_TABLE[nibble] * st.delta) >> 8;
        if (st.delta < 16) st.delta = 16;

        const idx = out + written[c];
        if (idx < total) channels[c][idx] = predicted / 32768;
        written[c]++;
      }
    }

    out += samplesPerBlock;
  }

  return channels;
}

// ── entry point ──────────────────────────────────────────────────────────────

/**
 * Decode a WAV file to its original channels and sample rate.
 *
 * @param {ArrayBuffer} arrayBuffer
 * @returns {{ channels: Float32Array[], sampleRate: number, formatTag: number, formatName: string }}
 * @throws {UnsupportedWavFormatError} when the container is valid but the codec isn't implemented
 */
export function decodeWav(arrayBuffer) {
  const header = parseWavHeader(arrayBuffer);

  if (!SUPPORTED.has(header.formatTag)) {
    throw new UnsupportedWavFormatError(header.formatTag);
  }

  const dv = new DataView(arrayBuffer);
  let channels;

  switch (header.formatTag) {
    case FORMAT_PCM:
    case FORMAT_IEEE_FLOAT:
      channels = decodePcm(dv, header);
      break;
    case FORMAT_MULAW:
    case FORMAT_IBM_MULAW:
      channels = decodeG711(dv, header, MULAW_TABLE);
      break;
    case FORMAT_ALAW:
    case FORMAT_IBM_ALAW:
      channels = decodeG711(dv, header, ALAW_TABLE);
      break;
    case FORMAT_IMA_ADPCM:
      channels = decodeImaAdpcm(dv, header);
      break;
    case FORMAT_OKI_ADPCM:
    case FORMAT_DIALOGIC_OKI:
      channels = decodeOkiAdpcm(dv, header);
      break;
    case FORMAT_MS_ADPCM:
      channels = decodeMsAdpcm(dv, header);
      break;
    default:
      throw new UnsupportedWavFormatError(header.formatTag);
  }

  if (!channels.length || !channels[0].length) {
    throw new Error('This WAV file contains no decodable audio.');
  }

  return {
    channels,
    sampleRate: header.sampleRate,
    formatTag: header.formatTag,
    formatName: formatName(header.formatTag),
  };
}
